// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure logic behind Settings > Mobile rail. The editor holds an
// ordered list of tab ids (max five, Ask implicit); rows are the chosen tabs
// in rail order followed by the rest in catalogue order.

import {
	MOBILE_MORE_APP_IDS,
	MOBILE_MORE_MAX_APPS,
	MOBILE_RAIL_MAX_TABS,
	MOBILE_RAIL_TAB_IDS,
	type MobileMoreAppId,
	type MobileRailTabId,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';

export interface MobileRailEditorRow {
	id: MobileRailTabId;
	on: boolean;
	/** Position on the rail when on; -1 otherwise. */
	index: number;
	canMoveUp: boolean;
	canMoveDown: boolean;
	/** Off rows cannot be turned on while the rail is full; the last on row cannot be turned off. */
	canToggle: boolean;
}

export interface MobileMoreAppEditorRow {
	id: MobileMoreAppId;
	on: boolean;
	index: number;
	onRail: boolean;
	canMoveUp: boolean;
	canMoveDown: boolean;
	canToggle: boolean;
}

export function isMobileRailFull(tabs: ReadonlyArray<MobileRailTabId>): boolean {
	return tabs.length >= MOBILE_RAIL_MAX_TABS;
}

export function mobileRailFreeSlots(tabs: ReadonlyArray<MobileRailTabId>): number {
	return Math.max(0, MOBILE_RAIL_MAX_TABS - tabs.length);
}

/** Adds the tab at the end or removes it. No-op when full (adding) or when it is the last tab (removing). */
export function toggleMobileRailTab(tabs: ReadonlyArray<MobileRailTabId>, id: MobileRailTabId): Array<MobileRailTabId> {
	if (tabs.includes(id)) {
		if (tabs.length <= 1) return [...tabs];
		return tabs.filter((tab) => tab !== id);
	}
	if (isMobileRailFull(tabs)) return [...tabs];
	return [...tabs, id];
}

/** Moves a chosen tab one step along the rail. No-op at the ends or for tabs that are off. */
export function moveMobileRailTab(
	tabs: ReadonlyArray<MobileRailTabId>,
	id: MobileRailTabId,
	direction: -1 | 1,
): Array<MobileRailTabId> {
	const from = tabs.indexOf(id);
	if (from === -1) return [...tabs];
	const to = from + direction;
	if (to < 0 || to >= tabs.length) return [...tabs];
	const next = [...tabs];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved as MobileRailTabId);
	return next;
}

export function mobileRailEditorRows(tabs: ReadonlyArray<MobileRailTabId>): Array<MobileRailEditorRow> {
	const full = isMobileRailFull(tabs);
	const on = tabs.map((id, index) => ({
		id,
		on: true,
		index,
		canMoveUp: index > 0,
		canMoveDown: index < tabs.length - 1,
		canToggle: tabs.length > 1,
	}));
	const off = MOBILE_RAIL_TAB_IDS.filter((id) => !tabs.includes(id)).map((id) => ({
		id,
		on: false,
		index: -1,
		canMoveUp: false,
		canMoveDown: false,
		canToggle: !full,
	}));
	return [...on, ...off];
}

export function isMobileMoreFull(apps: ReadonlyArray<MobileMoreAppId>): boolean {
	return apps.length >= MOBILE_MORE_MAX_APPS;
}

/** Adds or removes a More shortcut. Rail destinations are never duplicated in More. */
export function toggleMobileMoreApp(
	apps: ReadonlyArray<MobileMoreAppId>,
	tabs: ReadonlyArray<MobileRailTabId>,
	id: MobileMoreAppId,
): Array<MobileMoreAppId> {
	if (apps.includes(id)) return apps.filter((app) => app !== id);
	if (tabs.includes(id) || isMobileMoreFull(apps)) return [...apps];
	return [...apps, id];
}

export function moveMobileMoreApp(
	apps: ReadonlyArray<MobileMoreAppId>,
	id: MobileMoreAppId,
	direction: -1 | 1,
): Array<MobileMoreAppId> {
	const from = apps.indexOf(id);
	if (from === -1) return [...apps];
	const to = from + direction;
	if (to < 0 || to >= apps.length) return [...apps];
	const next = [...apps];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved as MobileMoreAppId);
	return next;
}

/** Selected shortcuts first, followed by available and on-rail destinations in catalogue order. */
export function mobileMoreAppEditorRows(
	apps: ReadonlyArray<MobileMoreAppId>,
	tabs: ReadonlyArray<MobileRailTabId>,
): Array<MobileMoreAppEditorRow> {
	const full = isMobileMoreFull(apps);
	const on = apps.map((id, index) => ({
		id,
		on: true,
		index,
		onRail: false,
		canMoveUp: index > 0,
		canMoveDown: index < apps.length - 1,
		canToggle: true,
	}));
	const off = MOBILE_MORE_APP_IDS.filter((id) => !apps.includes(id)).map((id) => {
		const onRail = tabs.includes(id);
		return {
			id,
			on: false,
			index: -1,
			onRail,
			canMoveUp: false,
			canMoveDown: false,
			canToggle: !onRail && !full,
		};
	});
	return [...on, ...off];
}
